import { useState, useEffect } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation, useNavigate } from 'react-router-dom';
import { PublicRoute } from './routing/RoutingLogic';
import { validateTokenAndRefresh } from './utils/authUtils';
import Install from './pages/Install';
import Main from './pages/Main';
import Login from './pages/Login'
import Register from './pages/Register'
import ResetPassword from './pages/ResetPassword';
import Info from './pages/Info';
import PrivacyStatement from './pages/PrivacyStatement';
import NotFound from './pages/NotFound';
import './styles/Main.css';
import './styles/Auth.css';
import './styles/Install.css';
import './styles/Modal.css';
import './styles/Loader.css';
import './styles/RequestList.css';
import './styles/Map.css';
import './styles/Card.css';
import './styles/Buttons.css';
import './styles/Checkbox.css';
import './styles/StatusCircle.css';
import './styles/PullToRefresh.css';
import './styles/Info.css';
import './styles/NewService.css'
import './styles/CategoryDropdown.css'

function InstallRedirect({ isPWA }: { isPWA: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hasRedirected = sessionStorage.getItem("redirectedToInstall");

    if (!isPWA && location.pathname === "/" && !hasRedirected) {
      sessionStorage.setItem("redirectedToInstall", "true");
      navigate("/install", { replace: true });
    }
  }, [isPWA, location.pathname, navigate]);

  return null;
}

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  // Check if user is logged in and refresh token on page load
  useEffect(() => {
    validateTokenAndRefresh(setIsLoggedIn);
  }, []);

  // Check if app is installed on page load
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
      (window.navigator as any).standalone === true;

    setIsPWA(isStandalone); 
  }, []);

  // Prevent scrolling when app is installed
  useEffect(() => {
    const applyNoScroll = () => {
      if (isPWA) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    };

    applyNoScroll();
    window.addEventListener('resize', applyNoScroll);

    return () => {
      window.removeEventListener('resize', applyNoScroll);
      document.body.classList.remove('no-scroll');
    };
  }, [isPWA]);

  return (
    <Router>
      <InstallRedirect isPWA={isPWA} />
      <Routes>
        {isPWA ? (
          <>
            <Route path="/login" element={<PublicRoute isLoggedIn={isLoggedIn} element={<Login setIsLoggedIn={setIsLoggedIn} />} />} />
            <Route path="/" element={<Main />} />
            <Route path="/install" element={<Navigate to="/login" />} />
            <Route path="/info" element={<Info />} />
          </>
        ) : (
          <>
            <Route path="/install" element={<PublicRoute isLoggedIn={isLoggedIn} element={<Install />} />} />
            <Route path="/login" element={<PublicRoute isLoggedIn={isLoggedIn} element={<Login setIsLoggedIn={setIsLoggedIn} />} />} />
            <Route path="/" element={<Main />} />
            <Route path="/info" element={<Info />} />
          </>
        )}
        <Route path="/register" element={<PublicRoute isLoggedIn={isLoggedIn} element={<Register setIsLoggedIn={setIsLoggedIn}/>} />} />
        <Route path="/resetpassword" element={<PublicRoute isLoggedIn={isLoggedIn} element={<ResetPassword />} />} />
        <Route path="/privacystatement" element={<PrivacyStatement />} />
        <Route path="*" element={<NotFound source='url'/>} />
      </Routes>
    </Router>
  );
}

export default App;